// Feature guard component for conditional rendering based on subscription features
import React from 'react';
import { Alert, Box, Button, Card, CardContent, Typography } from '@mui/material';
import { Lock as LockIcon, Upgrade as UpgradeIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { FEATURE_LABELS } from '../utils/featureGating';
import { ADMIN_FEATURES, PRO_TRIAL_DAYS, TOGGLEABLE_FEATURES, proTrialState } from '../constants/plans';
import UpgradeContactLink from './UpgradeContactLink';

const FeatureGuard = ({
  feature,
  children,
  fallback = null,
  showUpgradePrompt = true,
  minimal = false
}) => {
  const { checkFeature, tierAllows, tierName, loading, error, isExpired, subscriberData } = useFeatureAccess();
  const workspaceName = subscriberData?.name;
  
  // Show loading state
  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          Loading subscription data...
        </Typography>
      </Box>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }
  
  // Check if user has access to the feature
  const hasAccess = checkFeature(feature);
  
  if (hasAccess) {
    return children;
  }
  
  // If custom fallback is provided, use it
  if (fallback) {
    return fallback;
  }
  
  // Minimal fallback - just hide the content
  if (minimal) {
    return null;
  }
  
  // Default fallback UI
  const featureName = FEATURE_LABELS[feature] || 'This feature';
  
  if (isExpired) {
    return (
      <Card sx={{ m: 2, textAlign: 'center' }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <LockIcon color="error" sx={{ fontSize: 48 }} />
            <Typography variant="h6" color="error">
              Subscription Expired
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Your subscription has expired. To renew it and use {featureName.toLowerCase()} again,
              contact <UpgradeContactLink reason="renew" workspaceName={workspaceName} />.
            </Typography>
            {/* Renewal, like an upgrade, is something only TrakLog does — so the
                same contact, not "your administrator" in an alert(). */}
            {showUpgradePrompt && (
              <UpgradeContactLink button reason="renew" workspaceName={workspaceName}>
                Renew subscription
              </UpgradeContactLink>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }
  
  // Two different reasons land here, and they need different advice: the tier
  // does not include the feature (an upgrade, from TrakLog), or the tier does
  // but the workspace has not switched it on — Settings → Features for the
  // flags a workspace admin controls, TrakLog for the admin-set ones
  // (ADMIN_FEATURES), which no page in the workspace can switch.
  if (!tierAllows(feature)) {
    return (
      <Card sx={{ m: 2, textAlign: 'center' }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <LockIcon color="action" sx={{ fontSize: 48 }} />
            <Typography variant="h6">
              {featureName} is included in the Pro plan
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Your workspace is on the {tierName} plan. To use {featureName.toLowerCase()}, upgrade
              to Pro. Contact <UpgradeContactLink workspaceName={workspaceName} />.
            </Typography>
            {showUpgradePrompt && (
              <UpgradeContactLink button workspaceName={workspaceName} startIcon={<UpgradeIcon />} />
            )}
            {/* A Basic workspace that has not used this year's Pro trial can
                see the feature working before it decides. Started by an admin
                on Settings → Features; the API refuses anyone else. */}
            {showUpgradePrompt && proTrialState(subscriberData).canSelfStart && (
              <Button
                variant="outlined"
                color="secondary"
                component={RouterLink}
                to="/settings/features"
              >
                Try Pro free for {PRO_TRIAL_DAYS} days
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  const adminToggleable = TOGGLEABLE_FEATURES.includes(feature);
  const setByTrakLog = ADMIN_FEATURES.includes(feature);

  let explanation;
  if (adminToggleable) {
    explanation = `${featureName} is included in your plan but is not switched on for this workspace. An admin can switch it on in Settings → Features.`;
  } else if (setByTrakLog) {
    explanation = (
      <>
        {featureName} is included in your plan but is not switched on for this workspace. Only
        TrakLog can switch it on. Contact{' '}
        <UpgradeContactLink workspaceName={workspaceName} reason="enable" featureName={featureName} />{' '}
        to have it enabled.
      </>
    );
  } else {
    explanation = `${featureName} is not available for this workspace.`;
  }

  return (
    <Card sx={{ m: 2, textAlign: 'center' }}>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <LockIcon color="action" sx={{ fontSize: 48 }} />
          <Typography variant="h6">
            {featureName} Not Switched On
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {explanation}
          </Typography>
          {showUpgradePrompt && setByTrakLog && (
            <UpgradeContactLink
              button
              workspaceName={workspaceName}
              reason="enable"
              featureName={featureName}
            />
          )}
          {showUpgradePrompt && adminToggleable && (
            <Button
              variant="contained"
              color="primary"
              component={RouterLink}
              to="/settings/features"
              startIcon={<SettingsIcon />}
            >
              Open Features
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Higher-order component version
export const withFeatureGuard = (feature, options = {}) => {
  return (WrappedComponent) => {
    return (props) => {
      return (
        <FeatureGuard feature={feature} {...options}>
          <WrappedComponent {...props} />
        </FeatureGuard>
      );
    };
  };
};

// Navigation item wrapper - hides nav items if feature not available
export const FeatureNavItem = ({ feature, children, ...props }) => {
  const { checkFeature, loading } = useFeatureAccess();
  
  if (loading) {
    return null;
  }
  
  if (!checkFeature(feature)) {
    return null;
  }
  
  return children;
};

export default FeatureGuard;